import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

// Vérifier que les variables d'environnement sont bien définies
const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ Variables Supabase manquantes:', { supabaseUrl, supabaseAnonKey });
}

const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '');

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export default function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [endDate, setEndDate] = useState<Date | null>(null);

  useEffect(() => {
    const fetchCountdown = async () => {
      try {
        // Vérifier la connexion Supabase
        if (!supabaseUrl || !supabaseAnonKey) {
          console.error('❌ Configuration Supabase manquante');
          return;
        }

        const { data, error } = await supabase
          .from('countdown_settings')
          .select('end_date')
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error('❌ Erreur Supabase:', error);
          return;
        }

        if (data?.end_date) {
          const newDate = new Date(data.end_date);
          console.log('✅ Date chargée depuis la base:', newDate);
          setEndDate(newDate);
        } else {
          console.log('⚠️ Aucune date de fin trouvée dans la base');
        }
      } catch (err) {
        console.error('❌ Erreur lors de la récupération du compte à rebours:', err);
      }
    };

    fetchCountdown();

    // Écouter les changements en temps réel
    const channel = supabase
      .channel('countdown_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'countdown_settings',
        },
        (payload) => {
          console.log('🔄 Changement détecté:', payload);
          if (payload.new && (payload.new as any).end_date) {
            const newDate = new Date((payload.new as any).end_date);
            console.log('🎯 Nouvelle date appliquée:', newDate);
            setEndDate(newDate);
          }
        }
      )
      .subscribe((status) => {
        console.log('📡 Statut Realtime:', status);
      });

    // Polling de secours toutes les 5 secondes
    const pollingInterval = setInterval(() => {
      fetchCountdown();
    }, 5000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollingInterval);
    };
  }, []);

  useEffect(() => {
    if (!endDate) return;

    const calculateTimeLeft = () => {
      const difference = endDate.getTime() - new Date().getTime();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [endDate]);

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="text-center mb-4 sm:mb-6">
        <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-1 sm:mb-2">
          Clôture des inscriptions dans
        </h3>
      </div>
      
      <div className="grid grid-cols-4 gap-2 sm:gap-3 md:gap-4 lg:gap-6">
        {[
          { value: timeLeft.days, label: 'Jours' },
          { value: timeLeft.hours, label: 'Heures' },
          { value: timeLeft.minutes, label: 'Minutes' },
          { value: timeLeft.seconds, label: 'Secondes' },
        ].map((item, index) => (
          <div
            key={index}
            className="bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 border border-white/20 shadow-xl"
          >
            <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-1 sm:mb-2">
              {String(item.value).padStart(2, '0')}
            </div>
            <div className="text-xs sm:text-sm md:text-base text-white/80 font-medium uppercase tracking-wide">
              {item.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
